import { apiClient } from "@/services/api"
import { defineStore } from "pinia"
import { hasMoreRows } from "@/services/listAnswer"
import { requestVariants } from "@/services/supplierPrices"
import { isSupplierStrict } from "@/services/supplierMode"

let lastPage = -1

export const MATERIAL_FORM_NAMES = {
  GENERAL: "general",
  // Only once the workspace's prices are suppliers' (docs/suppliers.md).
  PURCHASE: "purchase",
  FILES: "files",
  ACTIVITY: "activity",
}

const _blankMaterial = () => {
  return {
    id: "",
    name: "",
    unit: "",
    purchasePrices: [],
    purchasePriceVariants: [],
    note: null,
    files: null,
    createdAt: null,
    createdBy: null,
    lastModifiedAt: null,
    lastModifiedBy: null,
  }
}

export const useMaterialStore = defineStore("material", {
  state: () => ({
    materials: [],
    material: _blankMaterial(),
  }),
  getters: {
    getMaterials: state => state.materials,
    getMaterial: state => state.material,
  },
  actions: {
    setMaterials(materials) {
      this.materials = [...materials]
    },
    addMaterials(materials) {
      materials.forEach(material => this.materials.push(material))
    },
    clearMaterials() {
      this.materials = []
    },
    setMaterial(material) {
      this.material = material
    },
    clearMaterial() {
      this.material = _blankMaterial()
    },
    updateMaterial(material) {
      if (material.purchasePrices && material.purchasePrices.length <= 0) {
        material.purchasePrices = null
      }
      // Once prices are suppliers' they change only at the supplier and in the Einkauf tab, which write at once:
      // the record's save leaves them as stored (null), rather than sending the rows this page loaded, which
      // the server would take for a stale tab's price edit (docs/suppliers.md).
      if (isSupplierStrict()) material = { ...material, purchasePriceVariants: null, purchasePrices: null }
      return apiClient
        .put(`/material/${material.id}`, material, { headers: { tenant: true, authorization: true } })
        .then(res => {
          if (res.status === 200) {
            this.setMaterial(res.data)
          }
          return res
        })
    },
    createMaterial(material, files = []) {
      material = { ...material, purchasePriceVariants: requestVariants(material.purchasePriceVariants) }
      if (material.purchasePrices && material.purchasePrices.length <= 0) {
        material.purchasePrices = null
      }
      if (files && files.length > 0) {
        const formData = new FormData()
        formData.append("material", new Blob([JSON.stringify(material)], { type: "application/json" }))
        files.forEach(file => formData.append("files", file))
        return apiClient
          .post("/material/with-files", formData, { headers: { tenant: true, authorization: true } })
          .then(res => {
            if (res.status === 200) this.setMaterial(res.data)
            return res
          })
      }
      return apiClient.post("/material", material, { headers: { tenant: true, authorization: true } }).then(res => {
        if (res.status === 200) {
          this.setMaterial(res.data)
        }
        return res
      })
    },
    loadMaterial(id) {
      return apiClient.get(`/material/${id}`, { headers: { tenant: true, authorization: true } }).then(res => {
        if (res.status === 200) {
          if (res.data.purchasePrices == null) {
            res.data.purchasePrices = []
          }
          this.setMaterial(res.data)
        }
        return res
      })
    },
    loadMaterials({ page, search, sortBy, sortDesc, selected, itemsPerPage, archive }) {
      return apiClient
        .get("/material", {
          params: {
            // Unset for the records in use; `ARCHIVED` for the overview's archive (docs/archiving.md).
            archive,
            page: page,
            search: search,
            sortBy: sortBy,
            sortDesc: sortDesc,
            selected: selected,
            itemsPerPage: itemsPerPage,
          },
          headers: { tenant: true, authorization: true },
        })
        .then(res => {
          if (res.status === 200) {
            if (page === 0) {
              this.setMaterials(res.data.content)
            } else if (lastPage === page) {
              this.setMaterials(res.data.content)
            } else if (lastPage === page - 1) {
              this.addMaterials(res.data.content)
            } else {
              this.setMaterials(res.data.content)
            }
            lastPage = page
          }
          return res
        })
    },
    /**
     * A page of the catalogue for a picker (`LewAutocompleteCatalog`), returned rather than written
     * into the store: the store's list is the material overview's rows, and a picker's search hits replaced them.
     * Silent, as every picker's search is: a snackbar per keystroke helps nobody.
     */
    searchMaterials({ search, page = 0, itemsPerPage }) {
      return apiClient
        .get("/material", {
          params: { page, itemsPerPage, search: search || undefined },
          headers: { tenant: true, authorization: true },
          silent: true,
        })
        .then(res => ({ items: res.data?.content ?? [], hasMore: hasMoreRows(res.data, itemsPerPage) }))
    },
  },
})
